import React, { forwardRef } from 'react';
import JsBarcode from 'jsbarcode';
import { Container } from '../../../core/interfaces/container.interface';

type Theme = 'mono' | 'green';

interface ModernContainerLabelProps {
  container: Container;
  categoryName?: string;
  typeName?: string;
  channelName?: string;
  priceRange?: string;
  theme?: Theme;
}

// Componente de barcode scannable que se imprime correctamente
function PrintableBarcode({ value }: { value: string }) {
  console.log('PrintableBarcode value:', value);

  // Generar barcode inmediatamente, sin useEffect
  let imageData = '';
  
  try {
    console.log('Generating barcode immediately for:', value);
    
    // Crear canvas temporal
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
             // Generar barcode en el canvas temporal
       JsBarcode(canvas, value, {
         format: "CODE128",
         width: 1.6,
         height: 38,
         displayValue: false,
         background: "#ffffff",
         lineColor: "#000000",
         margin: 0
       });
      
      // Convertir a imagen base64
      imageData = canvas.toDataURL('image/png');
      console.log('Barcode generated successfully as image');
    }
  } catch (error) {
    console.error('Error generating barcode:', error);
  }

  return (
         <div 
       className="barcode-wrap" 
       style={{ height: '10mm', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
     >
      {imageData ? (
                 <img
           src={imageData}
           alt={`Barcode ${value}`}
           style={{
             height: '8mm',
             maxWidth: '100%',
           }}
         />
      ) : (
                 <div style={{ 
           height: '8mm', 
           width: '100%', 
           background: '#f0f0f0', 
           display: 'flex', 
           alignItems: 'center', 
           justifyContent: 'center',
           fontSize: '2mm',
           color: '#666'
         }}>
           Error generando barcode
         </div>
      )}
    </div>
  );
}

const themes: Record<Theme, {
  bg: string;           // fondo etiqueta
  frameBorder: string;  // borde exterior
  bandBg: string;       // franja superior (fondo)
  bandColor: string;    // franja superior (texto)
  textColor: string;    // textos
  accent?: string;      // solo para green
}> = {
  mono: {
    bg: '#FFFFFF',
    frameBorder: '0.35mm solid #BDBDBD',
    bandBg: '#FFFFFF',
    bandColor: '#000000',
    textColor: '#111111',
  },
  green: {
    bg: '#FFFFFF',
    frameBorder: '0.35mm solid #7CB342',
    bandBg: '#2E7D32',
    bandColor: '#E8F5E9',
    textColor: '#1B5E20',
    accent: '#8BC34A',
  },
};

export const ModernContainerLabel = forwardRef<HTMLDivElement, ModernContainerLabelProps>(({ 
  container, 
  categoryName = 'CONTENEDOR', 
  typeName = 'DESTINO', 
  channelName = 'ECOMMERCE', 
  priceRange = '$0 - $20.000',
  theme = 'mono' 
}, ref) => {
  const t = themes[theme];

  const code = (container.code || 'ABCBOX0000001').toUpperCase();
  const formatDate = (date?: string | Date) => {
    const d = date ? new Date(date) : new Date();
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  return (
    <div
      ref={ref}
      style={{
        width: '60mm',
        height: '100mm',
        background: t.bg,
        boxSizing: 'border-box',
        padding: '1mm',
        position: 'relative',
        overflow: 'hidden',
        border: t.frameBorder,
      }}
      className="print-label print-label-60x100"
    >
                    {/* Header con código principal */}
       <div
         style={{
           backgroundColor: 'white',
           border: '2px solid black',
           padding: '1.2mm',
           textAlign: 'center',
           fontSize: '16px',
           fontWeight: 'bold',
           marginBottom: '0.7mm',
           height: '9mm',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
         }}
       >
         {code}
       </div>

       {/* Barcode real CODE128 */}
       <div
         style={{
           height: '14mm',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           padding: '0.7mm 2mm',
           marginBottom: '0.7mm',
         }}
       >
         <PrintableBarcode value={code} />
       </div>

       {/* Información del contenedor */}
       <div className="info-row">
         <div className="info-label">CATEGORÍA</div>
         <div className="info-value">{categoryName}</div>
       </div>

       <div className="info-row">
         <div className="info-label">TIPO CONTENEDOR</div>
         <div className="info-value">{typeName}</div>
       </div>

       <div className="info-row">
         <div className="info-label">FECHA CREACION</div>
         <div className="info-value">{formatDate()}</div>
       </div>

       <div className="info-row">
         <div className="info-label">RANGO</div>
         <div className="info-value">{priceRange}</div>
       </div>

       <div className="info-row">
         <div className="info-label">CANAL</div>
         <div className="info-value">{channelName}</div>
       </div>

       {/* Sección de observación manual */}
       <div
         style={{
           backgroundColor: 'white',
           border: '1px solid black',
           height: '14mm',
           margin: '0.7mm 0',
           padding: '0.7mm',
         }}
       >
         <div
           style={{
             fontWeight: 'bold',
             textAlign: 'center',
             fontSize: '8px',
             marginBottom: '0.7mm',
           }}
         >
           OBSERVACIÓN MANUAL
         </div>
         <div
           style={{
             backgroundColor: 'white',
             height: 'calc(100% - 2.5mm)',
             minHeight: '10mm',
             border: 'none',
             display: 'block',
           }}
         ></div>
       </div>

       {/* Secciones del footer */}
       <div style={{ display: 'flex', gap: '0.7mm', margin: '0.7mm 0' }}>
         <div
           style={{
             flex: 1,
             backgroundColor: 'white',
             border: '1px solid black',
             padding: '0.7mm',
             minHeight: '8mm',
           }}
         >
           <div
             style={{
               fontWeight: 'bold',
               textAlign: 'center',
               fontSize: '7px',
               marginBottom: '0.7mm',
             }}
           >
             REVISIÓN QA
           </div>
           <div
             style={{
               backgroundColor: 'white',
               height: 'calc(100% - 2.5mm)',
               minHeight: '5mm',
               border: 'none',
               display: 'block',
             }}
           ></div>
         </div>
         <div
           style={{
             flex: 1,
             backgroundColor: 'white',
             border: '1px solid black',
             padding: '0.7mm',
             minHeight: '8mm',
           }}
         >
           <div
             style={{
               fontWeight: 'bold',
               textAlign: 'center',
               fontSize: '7px',
               marginBottom: '0.7mm',
             }}
           >
             FECHA REVISIÓN
           </div>
           <div
             style={{
               backgroundColor: 'white',
               height: 'calc(100% - 2.5mm)',
               minHeight: '5mm',
               border: 'none',
               display: 'block',
             }}
           ></div>
         </div>
       </div>

       {/* QR al final - mismo que el de arriba */}
       <div
         style={{
           height: '14mm',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           padding: '0.7mm 2mm',
           marginTop: '0.7mm',
         }}
       >
         <PrintableBarcode value={code} />
       </div>

           </div>
   );
});

ModernContainerLabel.displayName = 'ModernContainerLabel';

export default ModernContainerLabel;
